import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { SignOut, House, FileText, Upload, Coins, Package, Key, Plus, Copy, XCircle, TrashSimple, ChartBar } from '@phosphor-icons/react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { toast } from 'sonner';
import AdminNav from './AdminNav';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminAgentKeys = () => {
  const navigate = useNavigate();
  const [keys, setKeys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [newKeyData, setNewKeyData] = useState({
    name: '', owner_email: '', quota_per_minute: 60, quota_per_day: 10000,
    shipping_profile: {
      full_name: '', company: '', street: '', city: '', state: '', zip_code: '', country: 'USA',
      contact_email: '', contact_phone: ''
    }
  });
  const [attachProfile, setAttachProfile] = useState(false);
  const [createdKey, setCreatedKey] = useState(null);
  const [usage, setUsage] = useState(null);
  const [viewingUsageFor, setViewingUsageFor] = useState(null);
  const [editingProfileFor, setEditingProfileFor] = useState(null);
  const [profileDraft, setProfileDraft] = useState(null);

  useEffect(() => {
    const token = localStorage.getItem('admin_token');
    if (!token) { navigate('/admin/login'); return; }
    loadKeys();
  }, []);

  const loadKeys = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('admin_token');
      const r = await axios.get(`${API_URL}/api/admin/agent-keys`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setKeys(r.data.keys || []);
    } catch (e) {
      toast.error('Failed to load keys');
      if (e.response?.status === 401) navigate('/admin/login');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    if (!newKeyData.name) return toast.error('Name required');
    try {
      const token = localStorage.getItem('admin_token');
      const payload = { ...newKeyData };
      if (!attachProfile) {
        delete payload.shipping_profile;
      }
      const r = await axios.post(`${API_URL}/api/admin/agent-keys`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCreatedKey(r.data);
      setShowCreate(false);
      setNewKeyData({
        name: '', owner_email: '', quota_per_minute: 60, quota_per_day: 10000,
        shipping_profile: { full_name: '', company: '', street: '', city: '', state: '', zip_code: '', country: 'USA', contact_email: '', contact_phone: '' }
      });
      setAttachProfile(false);
      loadKeys();
    } catch (e) {
      toast.error('Failed to create key');
    }
  };

  const openEditProfile = (k) => {
    setEditingProfileFor(k);
    setProfileDraft(k.shipping_profile || {
      full_name: '', company: '', street: '', city: '', state: '', zip_code: '', country: 'USA',
      contact_email: '', contact_phone: ''
    });
  };

  const saveProfile = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      await axios.put(`${API_URL}/api/admin/agent-keys/${editingProfileFor.id}/shipping-profile`, profileDraft, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Shipping profile saved');
      setEditingProfileFor(null);
      setProfileDraft(null);
      loadKeys();
    } catch (e) {
      toast.error('Failed to save profile');
    }
  };

  const revokeKey = async (id) => {
    if (!window.confirm('Revoke this key? Active agents will be disconnected.')) return;
    try {
      const token = localStorage.getItem('admin_token');
      await axios.put(`${API_URL}/api/admin/agent-keys/${id}/revoke`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Key revoked');
      loadKeys();
    } catch (e) {
      toast.error('Failed to revoke');
    }
  };

  const deleteKey = async (id) => {
    if (!window.confirm('Permanently delete this key and its usage history?')) return;
    try {
      const token = localStorage.getItem('admin_token');
      await axios.delete(`${API_URL}/api/admin/agent-keys/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Key deleted');
      loadKeys();
    } catch (e) {
      toast.error('Failed to delete');
    }
  };

  const viewUsage = async (key) => {
    setViewingUsageFor(key);
    try {
      const token = localStorage.getItem('admin_token');
      const r = await axios.get(`${API_URL}/api/admin/agent-keys/${key.id}/usage?hours=24`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUsage(r.data);
    } catch (e) {
      toast.error('Failed to load usage');
      setViewingUsageFor(null);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('admin_token');
    navigate('/admin/login');
  };

  const copyKey = (text) => {
    navigator.clipboard?.writeText(text);
    toast.success('Copied to clipboard');
  };

  return (
    <div className="min-h-screen bg-[#05050A]">
      <AdminNav title="Agent API Keys" titleTestId="agent-keys-title" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-xl font-['JetBrains_Mono'] font-bold">Issue keys to AI agents & partners</h2>
            <p className="text-sm text-[#94A3B8] mt-1">
              Keys gate writes on <code className="text-cyan-400">/api/agent/orders/create</code> and <code className="text-cyan-400">/api/agent/orders/submit-rfq</code>. Public read endpoints stay open for discovery.
            </p>
          </div>
          <Button onClick={() => setShowCreate(true)} className="bg-[#10B981]" data-testid="new-key-btn">
            <Plus size={18} className="mr-1" /> New Key
          </Button>
        </div>

        {loading ? (
          <p className="text-center text-[#94A3B8] py-8">Loading…</p>
        ) : keys.length === 0 ? (
          <div className="bg-[#11141D] border border-[#1E293B] rounded-lg p-12 text-center">
            <Key size={48} className="mx-auto text-[#94A3B8] mb-3" />
            <p className="text-[#94A3B8]">No API keys yet. Create one to onboard an AI agent or partner.</p>
          </div>
        ) : (
          <div className="space-y-3" data-testid="keys-list">
            {keys.map(k => (
              <div key={k.id} className={`bg-[#11141D] border rounded-lg p-4 ${k.active ? 'border-[#1E293B]' : 'border-[#EF4444]/40 opacity-60'}`}>
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="font-['JetBrains_Mono'] font-bold">{k.name}</p>
                      {k.active
                        ? <span className="px-2 py-0.5 rounded text-xs font-bold bg-[#10B981]/20 text-[#10B981]">ACTIVE</span>
                        : <span className="px-2 py-0.5 rounded text-xs font-bold bg-[#EF4444]/20 text-[#EF4444]">REVOKED</span>}
                      {k.tags?.map(t => <span key={t} className="px-2 py-0.5 rounded text-xs bg-[#007AFF]/20 text-[#007AFF]">{t}</span>)}
                    </div>
                    <p className="font-mono text-xs text-cyan-400 mt-1">{k.prefix}••••••••••••••••••••••••••••••••</p>
                    {k.owner_email && <p className="text-xs text-[#94A3B8] mt-1">{k.owner_email}</p>}
                    <div className="flex gap-4 mt-2 text-xs text-[#94A3B8]">
                      <span>Quota: {k.quota_per_minute}/min · {k.quota_per_day}/day</span>
                      <span>Total: {k.total_requests}</span>
                      <span className="text-[#10B981]">Last 24h: {k.requests_last_24h}</span>
                      {k.last_used_at && <span>Last used: {new Date(k.last_used_at).toLocaleString()}</span>}
                    </div>
                    {k.shipping_profile && (
                      <p className="text-xs text-cyan-400 mt-1">
                        📦 Default ship-to: {k.shipping_profile.full_name || '—'}, {k.shipping_profile.city || '—'}, {k.shipping_profile.country || '—'}
                      </p>
                    )}
                  </div>
                  <div className="flex gap-2">
                    <Button onClick={() => viewUsage(k)} variant="outline" size="sm" className="border-[#334155]" data-testid={`usage-${k.id}`}>
                      <ChartBar size={14} className="mr-1" /> Usage
                    </Button>
                    <Button onClick={() => openEditProfile(k)} variant="outline" size="sm" className="border-[#007AFF] text-[#007AFF]" data-testid={`profile-${k.id}`}>
                      Ship Profile {k.shipping_profile ? '✓' : '+'}
                    </Button>
                    {k.active && (
                      <Button onClick={() => revokeKey(k.id)} variant="outline" size="sm" className="border-[#F59E0B] text-[#F59E0B]" data-testid={`revoke-${k.id}`}>
                        <XCircle size={14} className="mr-1" /> Revoke
                      </Button>
                    )}
                    <Button onClick={() => deleteKey(k.id)} variant="outline" size="sm" className="border-[#EF4444] text-[#EF4444]" data-testid={`delete-${k.id}`}>
                      <TrashSimple size={14} />
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Integration help */}
        <div className="mt-8 bg-[#11141D] border border-[#1E293B] rounded-lg p-6">
          <h3 className="font-['JetBrains_Mono'] text-[#007AFF] mb-3">Usage example (for your agent)</h3>
          <pre className="bg-[#05050A] rounded p-4 text-xs overflow-x-auto text-[#10B981]">
{`curl -X POST "${API_URL}/api/agent/orders/submit-rfq" \\
  -H "X-Agent-API-Key: sma_your_key_here" \\
  -H "Content-Type: application/json" \\
  -d '{
    "wallet_address": "0x...",
    "chain": "base",
    "contact": { "email": "agent@acme.ai", "phone": "+1..." },
    "shipping_address": { "full_name": "Acme Bot" },
    "items": [{ "part_number": "HMA81GS6DJR8N-XN", "quantity": 100 }]
  }'`}
          </pre>
        </div>
      </div>

      {/* Create Dialog */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="max-w-md bg-[#11141D] border-[#1E293B] text-white max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-['JetBrains_Mono'] text-[#10B981]">Create API Key</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs text-[#94A3B8]">Name *</Label>
              <Input value={newKeyData.name} onChange={(e) => setNewKeyData({ ...newKeyData, name: e.target.value })}
                placeholder="e.g., Acme Procurement Bot"
                className="bg-transparent border-[#334155]" data-testid="create-name" />
            </div>
            <div>
              <Label className="text-xs text-[#94A3B8]">Owner Email</Label>
              <Input value={newKeyData.owner_email} onChange={(e) => setNewKeyData({ ...newKeyData, owner_email: e.target.value })}
                className="bg-transparent border-[#334155]" data-testid="create-email" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs text-[#94A3B8]">Quota / minute</Label>
                <Input type="number" value={newKeyData.quota_per_minute}
                  onChange={(e) => setNewKeyData({ ...newKeyData, quota_per_minute: parseInt(e.target.value) || 60 })}
                  className="bg-transparent border-[#334155]" />
              </div>
              <div>
                <Label className="text-xs text-[#94A3B8]">Quota / day</Label>
                <Input type="number" value={newKeyData.quota_per_day}
                  onChange={(e) => setNewKeyData({ ...newKeyData, quota_per_day: parseInt(e.target.value) || 10000 })}
                  className="bg-transparent border-[#334155]" />
              </div>
            </div>

            <div className="pt-2 border-t border-[#1E293B]">
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={attachProfile} onChange={(e) => setAttachProfile(e.target.checked)} data-testid="attach-profile-toggle" />
                <span className="text-sm text-white">Attach default shipping profile</span>
              </label>
              <p className="text-xs text-[#94A3B8] mt-1">When set, orders from this key auto-use these details unless overridden in the request body.</p>
            </div>

            {attachProfile && (
              <ShippingProfileForm
                value={newKeyData.shipping_profile}
                onChange={(p) => setNewKeyData({ ...newKeyData, shipping_profile: p })}
                testidPrefix="create-sp"
              />
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreate(false)} className="border-[#334155]">Cancel</Button>
            <Button onClick={handleCreate} className="bg-[#10B981]" data-testid="create-key-confirm">Create</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reveal created key */}
      <Dialog open={!!createdKey} onOpenChange={(o) => !o && setCreatedKey(null)}>
        <DialogContent className="max-w-md bg-[#11141D] border-[#10B981] text-white">
          <DialogHeader><DialogTitle className="font-['JetBrains_Mono'] text-[#10B981]">✓ Key Created</DialogTitle></DialogHeader>
          {createdKey && (
            <div className="space-y-3">
              <p className="text-[#F59E0B] text-sm font-bold">⚠ Copy this key NOW. It will never be shown again.</p>
              <div className="bg-[#05050A] rounded p-3 flex items-center gap-2">
                <code className="font-mono text-xs text-[#10B981] break-all flex-1" data-testid="revealed-key">{createdKey.api_key}</code>
                <Button onClick={() => copyKey(createdKey.api_key)} size="sm" variant="outline" className="border-[#334155]">
                  <Copy size={14} />
                </Button>
              </div>
              <p className="text-xs text-[#94A3B8]">Key: <strong>{createdKey.key_record.name}</strong><br />
                Quota: {createdKey.key_record.quota_per_minute}/min · {createdKey.key_record.quota_per_day}/day</p>
            </div>
          )}
          <DialogFooter>
            <Button onClick={() => setCreatedKey(null)} className="bg-[#0047AB]" data-testid="close-reveal">I've saved it</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Usage dialog */}
      <Dialog open={!!viewingUsageFor} onOpenChange={(o) => !o && setViewingUsageFor(null)}>
        <DialogContent className="max-w-2xl bg-[#11141D] border-[#1E293B] text-white max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-['JetBrains_Mono'] text-[#007AFF]">Usage — {viewingUsageFor?.name}</DialogTitle>
          </DialogHeader>
          {usage && (
            <div className="space-y-4 text-sm">
              <p className="text-[#94A3B8]">Last {usage.window_hours}h — <strong className="text-[#10B981]">{usage.total_requests}</strong> requests</p>
              <div>
                <h4 className="font-['JetBrains_Mono'] text-[#007AFF] text-sm mb-2">By endpoint</h4>
                <div className="bg-[#05050A] rounded p-3 space-y-1 text-xs">
                  {Object.entries(usage.by_endpoint || {}).map(([ep, count]) => (
                    <div key={ep} className="flex justify-between">
                      <code className="text-cyan-400">{ep}</code>
                      <span className="text-[#10B981] font-bold">{count}</span>
                    </div>
                  ))}
                  {Object.keys(usage.by_endpoint || {}).length === 0 && <p className="text-[#94A3B8]">No activity.</p>}
                </div>
              </div>
              <div>
                <h4 className="font-['JetBrains_Mono'] text-[#007AFF] text-sm mb-2">Recent requests</h4>
                <div className="bg-[#05050A] rounded p-2 max-h-64 overflow-y-auto text-xs space-y-1">
                  {(usage.recent || []).map((r, i) => (
                    <div key={i} className="flex justify-between gap-2 border-b border-[#1E293B] py-1 last:border-0">
                      <span className={r.status_code >= 400 ? 'text-[#EF4444]' : 'text-[#10B981]'}>{r.method} {r.status_code}</span>
                      <code className="text-cyan-400 flex-1 truncate">{r.endpoint}</code>
                      <span className="text-[#94A3B8]">{new Date(r.timestamp).toLocaleTimeString()}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
      {/* Edit Profile Dialog */}
      <Dialog open={!!editingProfileFor} onOpenChange={(o) => !o && setEditingProfileFor(null)}>
        <DialogContent className="max-w-md bg-[#11141D] border-[#1E293B] text-white max-h-[90vh] overflow-y-auto" data-testid="edit-profile-dialog">
          <DialogHeader>
            <DialogTitle className="font-['JetBrains_Mono'] text-[#007AFF]">
              Shipping Profile — {editingProfileFor?.name}
            </DialogTitle>
          </DialogHeader>
          <p className="text-xs text-[#94A3B8]">
            Orders placed with this key will auto-fill these fields if not provided in the request.
          </p>
          {profileDraft && (
            <ShippingProfileForm value={profileDraft} onChange={setProfileDraft} testidPrefix="edit-sp" />
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditingProfileFor(null)} className="border-[#334155]">Cancel</Button>
            <Button onClick={saveProfile} className="bg-[#007AFF]" data-testid="save-profile-btn">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

const ShippingProfileForm = ({ value, onChange, testidPrefix = 'sp' }) => {
  const set = (k, v) => onChange({ ...value, [k]: v });
  return (
    <div className="space-y-2">
      <Input placeholder="Full name / Attn" value={value.full_name || ''} onChange={(e) => set('full_name', e.target.value)} className="bg-transparent border-[#334155]" data-testid={`${testidPrefix}-fullname`} />
      <Input placeholder="Company" value={value.company || ''} onChange={(e) => set('company', e.target.value)} className="bg-transparent border-[#334155]" data-testid={`${testidPrefix}-company`} />
      <Input placeholder="Street" value={value.street || ''} onChange={(e) => set('street', e.target.value)} className="bg-transparent border-[#334155]" data-testid={`${testidPrefix}-street`} />
      <div className="grid grid-cols-3 gap-2">
        <Input placeholder="City" value={value.city || ''} onChange={(e) => set('city', e.target.value)} className="bg-transparent border-[#334155]" data-testid={`${testidPrefix}-city`} />
        <Input placeholder="State" value={value.state || ''} onChange={(e) => set('state', e.target.value)} className="bg-transparent border-[#334155]" data-testid={`${testidPrefix}-state`} />
        <Input placeholder="ZIP" value={value.zip_code || ''} onChange={(e) => set('zip_code', e.target.value)} className="bg-transparent border-[#334155]" data-testid={`${testidPrefix}-zip`} />
      </div>
      <Input placeholder="Country" value={value.country || 'USA'} onChange={(e) => set('country', e.target.value)} className="bg-transparent border-[#334155]" data-testid={`${testidPrefix}-country`} />
      <div className="grid grid-cols-2 gap-2 pt-2 border-t border-[#1E293B]">
        <Input placeholder="Contact email" value={value.contact_email || ''} onChange={(e) => set('contact_email', e.target.value)} className="bg-transparent border-[#334155]" data-testid={`${testidPrefix}-email`} />
        <Input placeholder="Contact phone" value={value.contact_phone || ''} onChange={(e) => set('contact_phone', e.target.value)} className="bg-transparent border-[#334155]" data-testid={`${testidPrefix}-phone`} />
      </div>
    </div>
  );
};

export default AdminAgentKeys;
